import { useEffect, useLayoutEffect, useReducer, useRef, type AnimationEvent, type ReactNode } from "react";

interface PageTransitionProps {
  children: ReactNode;
  pageKey: string;
}

type Previous = { key: string; slot: 0 | 1; node: ReactNode; scroll: number };

/**
 * A route change is a crossfade: the incoming page fades in OVER the page the
 * visitor is looking at, and that page is only removed once the new one has
 * settled. At no instant is the canvas blank.
 *
 * Two slots. Each page is rendered in one of two stable wrapper divs, and a
 * route change never moves a page between them: the outgoing page stays in the
 * slot it has always been in (its element frozen in a ref, so React keeps its
 * DOM and runs none of its effects again) while that slot's wrapper becomes
 * `.pf-route-old`, a fixed, viewport-sized scroll container beneath everything
 * else with its scrollTop set to where the visitor was, sticky header included.
 * The incoming page mounts in the other slot, in normal flow above it
 * (`.pf-route-live`, position: relative; z-index: 1), resets the document
 * scroll in its own layout effect, and runs its enter (pf-route-in /
 * pf-route-fade, adjacent.css) from opacity 0 on --pf-fast. On a cold trip the
 * Suspense fallback mounts in the live slot first, so the visitor keeps the old
 * page under a transparent loader while the chunk arrives instead of a white
 * screen with a spinner.
 *
 * The old slot is emptied when the live slot's enter animation ends, or at once
 * when RouteEnter reports it has no entrance to play (`pfrouteenter`: reduced
 * motion, or a hard load), with a 3s cap so a chunk that never arrives cannot
 * pin the old page forever.
 *
 * This replaced an AnimatePresence `mode="wait"` fade-to-white: the outgoing
 * page faded to 0, then for one painted frame came back at full opacity (a
 * WAAPI exit over an inline `opacity: 1` with no forward fill) before the
 * incoming page began from 0, so every route change carried a pop and a
 * quarter-second of white. A first crossfade that moved the outgoing element
 * into an underlay div made React remount it, so it faded in from white and
 * re-ran its effects; hence the slots.
 */
export function PageTransition({ children, pageKey }: PageTransitionProps) {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const current = useRef<{ key: string; slot: 0 | 1 }>({ key: pageKey, slot: 0 });
  const previous = useRef<Previous | null>(null);
  const lastChildren = useRef(children);
  const wrappers = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)] as const;

  // The key changed: the page that was live becomes the old layer, frozen as
  // the element it last rendered. Done during render on purpose and idempotent
  // per key: the incoming page's layout effect resets the document scroll
  // before any effect of this component could read where the visitor was.
  if (current.current.key !== pageKey) {
    previous.current = {
      key: current.current.key,
      slot: current.current.slot,
      node: lastChildren.current,
      scroll: typeof window === "undefined" ? 0 : window.scrollY,
    };
    current.current = { key: pageKey, slot: current.current.slot === 0 ? 1 : 0 };
  }
  lastChildren.current = children;
  const prev = previous.current;
  const live = current.current.slot;

  useLayoutEffect(() => {
    wrappers.forEach((ref, i) => {
      const el = ref.current;
      if (!el) return;
      if (prev && i === prev.slot) {
        // The link the visitor just activated still holds focus inside the old page;
        // release it before the subtree is hidden from assistive technology.
        if (el.contains(document.activeElement)) (document.activeElement as HTMLElement | null)?.blur();
        el.scrollTop = prev.scroll; el.setAttribute("inert", ""); el.setAttribute("aria-hidden", "true");
      } else { el.removeAttribute("inert"); el.removeAttribute("aria-hidden"); }
    });
  });

  useEffect(() => {
    if (!prev) return;
    const settle = () => { if (previous.current) { previous.current = null; rerender(); } };
    // An entrance-less page (reduced motion) has nothing to wait for, but it has
    // not composited yet when its effects run: releasing the old page in the same
    // frame painted one frame of paper. Two frames later the new page is on screen.
    let frame = 0;
    const liveEl = wrappers[live].current;
    const reveal = () => { if (liveEl) liveEl.style.visibility = ""; settle(); };
    const settleAfterPaint = () => {
      if (!liveEl) return settle();
      // Keep the old page on screen and the new one hidden until the new page's
      // photograph has decoded and two frames have passed; then swap in one frame.
      liveEl.style.visibility = "hidden";
      const img = liveEl.querySelector("img");
      const loaded = img && !img.complete
        ? new Promise<void>(resolve => { img.addEventListener("load", () => resolve(), { once: true }); img.addEventListener("error", () => resolve(), { once: true }); })
        : Promise.resolve();
      loaded.then(() => (img && img.decode ? img.decode().catch(() => undefined) : undefined)).then(() => {
        frame = requestAnimationFrame(() => { frame = requestAnimationFrame(reveal); });
      });
    };
    const timer = window.setTimeout(reveal, 3000);
    window.addEventListener("pfrouteenter", settleAfterPaint);
    // RouteEnter's own effect runs before this one, so an entrance-less page
    // that mounted in the same commit has already dispatched; read its attribute
    // instead of waiting for an event that has passed.
    const entered = wrappers[live].current?.querySelector(".pf-route-enter");
    if (entered && !entered.hasAttribute("data-entrance")) settleAfterPaint();
    return () => { window.clearTimeout(timer); cancelAnimationFrame(frame); if (liveEl) liveEl.style.visibility = ""; window.removeEventListener("pfrouteenter", settleAfterPaint); };
  }, [prev, live]);

  const onLiveAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (previous.current && (event.animationName === "pf-route-in" || event.animationName === "pf-route-fade")) {
      previous.current = null;
      rerender();
    }
  };

  return (
    <div className="pf-route-stage">
      {([0, 1] as const).map(i => {
        const isLive = i === live;
        const isOld = prev !== null && i === prev.slot;
        return (
          <div
            key={i}
            ref={wrappers[i]}
            className={isOld ? "pf-route-old" : "pf-route-live"}
            onAnimationEnd={isLive ? onLiveAnimationEnd : undefined}
          >
            {isLive ? children : isOld ? prev.node : null}
          </div>
        );
      })}
    </div>
  );
}
